import React, { useEffect, useState } from "react";
import { AlertCircle, FileText, Landmark, TrendingUp, Wallet } from "lucide-react";

import api from "../api";

function AccountsHome() {
  const [summary, setSummary] = useState({ by_status: {}, total_outstanding: 0 });
  const [outstanding, setOutstanding] = useState({ data: [], total_outstanding: 0, total_overdue: 0 });
  const [profitLoss, setProfitLoss] = useState({ net_profit: 0, revenue: { total: 0 }, expenses: { total: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [summaryRes, outstandingRes, plRes] = await Promise.all([
          api.get("/accounts/invoices/summary"),
          api.get("/accounts/party/outstanding-report"),
          api.get("/accounts/reports/profit-loss"),
        ]);
        setSummary(summaryRes.data || { by_status: {}, total_outstanding: 0 });
        setOutstanding(outstandingRes.data || { data: [], total_outstanding: 0, total_overdue: 0 });
        setProfitLoss(plRes.data || { net_profit: 0, revenue: { total: 0 }, expenses: { total: 0 } });
        setError("");
      } catch (err) {
        setError(err.response?.data?.error || "Failed to load accounts dashboard.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <div className="rounded-3xl border border-slate-200 bg-white p-8">Loading account dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      {error && <ErrorBanner message={error} />}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={Wallet} title="Outstanding Receivables" value={summary.total_outstanding || 0} tone="amber" />
        <StatCard icon={FileText} title="Draft Invoices" value={summary.by_status?.DRAFT?.count || 0} suffix="docs" tone="indigo" />
        <StatCard icon={TrendingUp} title="Net Profit" value={profitLoss.net_profit || 0} tone="emerald" />
        <StatCard icon={Landmark} title="Monthly Revenue" value={profitLoss.revenue?.total || 0} tone="blue" />
      </div>

      <section className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-5">
            <h3 className="text-xl font-bold text-slate-900">Outstanding Parties</h3>
            <p className="text-sm text-slate-500">Live receivables and overdue invoices from the party ledger API.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Outstanding</th>
                  <th className="px-4 py-3">Overdue</th>
                  <th className="px-4 py-3">Oldest Due</th>
                  <th className="px-4 py-3">Rating</th>
                </tr>
              </thead>
              <tbody>
                {(outstanding.data || []).slice(0, 8).map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{row.name}</div>
                      <div className="text-xs text-slate-500">{row.code}</div>
                    </td>
                    <td className="px-4 py-3 font-semibold">Rs. {Number(row.outstanding || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-rose-700 font-semibold">Rs. {Number(row.overdue_amount || 0).toLocaleString()}</td>
                    <td className="px-4 py-3">{row.oldest_overdue_days || 0} days</td>
                    <td className="px-4 py-3">{row.payment_rating || 0}/5</td>
                  </tr>
                ))}
                {!outstanding.data?.length && (
                  <tr>
                    <td colSpan="5" className="px-4 py-10 text-center text-slate-500">No outstanding receivables.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-6">
          <SummaryPanel
            title="Invoice Pipeline"
            rows={[
              ["Draft", summary.by_status?.DRAFT?.count || 0],
              ["Pending Approval", summary.by_status?.PENDING_APPROVAL?.count || 0],
              ["Approved", summary.by_status?.APPROVED?.count || 0],
              ["Sent", summary.by_status?.SENT?.count || 0],
              ["Partially Paid", summary.by_status?.PARTIALLY_PAID?.count || 0],
              ["Paid", summary.by_status?.PAID?.count || 0],
            ]}
          />
          <SummaryPanel
            title="P&L Snapshot"
            rows={[
              ["Revenue", `Rs. ${Number(profitLoss.revenue?.total || 0).toLocaleString()}`],
              ["Expenses", `Rs. ${Number(profitLoss.expenses?.total || 0).toLocaleString()}`],
              ["Net Profit", `Rs. ${Number(profitLoss.net_profit || 0).toLocaleString()}`],
              ["Overdue", `Rs. ${Number(outstanding.total_overdue || 0).toLocaleString()}`],
            ]}
          />
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, title, value, suffix = "", tone = "indigo" }) {
  const toneMap = {
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    blue: "bg-blue-50 text-blue-700 border-blue-100",
  };
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-4">
        <div className={`flex h-12 w-12 items-center justify-center rounded-2xl border ${toneMap[tone]}`}>
          <Icon size={22} />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</div>
          <div className="text-2xl font-bold text-slate-900">
            {suffix ? `${value} ${suffix}` : `Rs. ${Number(value || 0).toLocaleString()}`}
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryPanel({ title, rows }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-4 text-lg font-bold text-slate-900">{title}</h3>
      <div className="space-y-3">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between text-sm text-slate-700">
            <span>{label}</span>
            <span className="font-semibold">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div className="flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
      <AlertCircle size={16} />
      <span>{message}</span>
    </div>
  );
}

export default AccountsHome;
